import React, { useState, useEffect } from 'react';
import HazardMap from './components/HazardMap.tsx';
import Controls from './components/Controls.tsx';
import { HazardData, HazardMapData } from './types/HazardData';
import './App.css';

const fetchJson = async (url: string) => {
  const response = await fetch(url);
  const data = await response.json();

  if (!response.ok || !data.success) {
    throw new Error(data.message || data.error || `Request failed with status ${response.status}`);
  }

  return data;
};

function App() {
  // Configurazione API dinamica basata sull'ambiente
  const getApiBaseUrl = () => {
    const currentHost = window.location.host;
    const currentProtocol = window.location.protocol;
    const currentPathname = window.location.pathname;
    
    // Se siamo in reverse proxy (vo-tcs.ct.ingv.it)
    if (currentHost.includes('vo-tcs.ct.ingv.it')) {
      return `${currentProtocol}//${currentHost}/geoportal/staging`;
    }
    
    // Se siamo su localhost o IP locale con /geoportal/staging/
    if (currentPathname.startsWith('/geoportal/staging/')) {
      return `${currentProtocol}//${currentHost}/geoportal/staging`;
    }
    
    // Fallback per sviluppo locale
    return `${currentProtocol}//${currentHost}`;
  };
  
  const API_BASE_URL = getApiBaseUrl();

  const [percentiles, setPercentiles] = useState<number[]>([]);
  const [ashLoads, setAshLoads] = useState<number[]>([]);
  const [seismicIntensities, setSeismicIntensities] = useState<number[]>([]);
  const [selectedPercentile, setSelectedPercentile] = useState<number | null>(null);
  const [selectedAshLoad, setSelectedAshLoad] = useState<number | null>(null);
  const [selectedSeismicIntensity, setSelectedSeismicIntensity] = useState<number | null>(null);
  const [thresProb, setThresProb] = useState<number>(0.1);
  const [mapMode, setMapMode] = useState<'probability' | 'hazard' | 'wms'>('probability');
  const [hazardType, setHazardType] = useState<'ash' | 'lava' | 'seismic'>('ash');
  const [exposureTime, setExposureTime] = useState<number>(30);
  const [opacity, setOpacity] = useState<number>(0.7);
  const [hazardData, setHazardData] = useState<HazardData[]>([]);
  const [hazardMapData, setHazardMapData] = useState<HazardMapData[]>([]);
  const [lavaData, setLavaData] = useState<HazardData[]>([]);
  const [seismicData, setSeismicData] = useState<HazardData[]>([]);
  const [seismicWmsData, setSeismicWmsData] = useState<any>(null);
  const [selectedSeismicThreshold, setSelectedSeismicThreshold] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloadFormat, setDownloadFormat] = useState<'png' | 'geotiff' | 'geojson'>('png');
  const [bbox, setBbox] = useState<string>('');

  // Load available percentiles and ash loads on component mount
  useEffect(() => {
    const loadOptions = async () => {
      try {
        // Load different data based on hazard type
        const endpoint = hazardType === 'ash' ? `${API_BASE_URL}/api/percentiles` : 
                        hazardType === 'seismic' ? `${API_BASE_URL}/api/seismic-percentiles` : 
                        `${API_BASE_URL}/api/lava-percentiles`;
        
        const requests = [fetchJson(endpoint)];
        if (hazardType === 'ash') {
          requests.push(fetchJson(`${API_BASE_URL}/api/ash-loads`));
        } else if (hazardType === 'seismic') {
          requests.push(fetchJson(`${API_BASE_URL}/api/seismic-intensities`));
        }
        
        const [percentilesData, secondaryData] = await Promise.all(requests);
        
        setPercentiles(percentilesData.percentiles);
        setSelectedPercentile(percentilesData.percentiles[0] ?? null);

        // Load specific data based on hazard type
        if (hazardType === 'ash' && secondaryData) {
          setAshLoads(secondaryData.ashLoads);
          setSelectedAshLoad(secondaryData.ashLoads[0] ?? null);
          // Clear seismic data
          setSeismicIntensities([]);
          setSelectedSeismicIntensity(null);
        } else if (hazardType === 'seismic' && secondaryData) {
          setSeismicIntensities(secondaryData.intensities);
          setSelectedSeismicIntensity(secondaryData.intensities[0] ?? null);
          // Clear ash data
          setAshLoads([]);
          setSelectedAshLoad(null);
        } else {
          // Clear both for lava type
          setAshLoads([]);
          setSelectedAshLoad(null);
          setSeismicIntensities([]);
          setSelectedSeismicIntensity(null);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unknown error';
        setError(`Failed to load options: ${message}`);
        console.error('Error loading options:', err);
      }
    };

    loadOptions();
  }, [hazardType]); // Re-load when hazard type changes

  // Auto-loading removed - data loads only when Execute Query is clicked

  // Load probability data
  const loadHazardData = async () => {
    if (selectedPercentile === null || selectedAshLoad === null) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let url = `${API_BASE_URL}/api/hazard-data?percentile=${selectedPercentile}&ashLoad=${selectedAshLoad}`;
      if (bbox.trim()) {
        url += `&bbox=${encodeURIComponent(bbox.trim())}`;
      }
      
      const response = await fetch(url);      const data = await response.json();

      if (data.success) {
        setHazardData(data.data);
        setHazardMapData([]); // Clear hazard map data
      } else {
        setError(data.error || 'Failed to load hazard data');
      }
    } catch (err) {
      setError('Failed to load hazard data');
      console.error('Error loading hazard data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load hazard map data
  const loadHazardMapData = async () => {
    if (selectedPercentile === null) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let url = `${API_BASE_URL}/api/hazard-map?percentile=${selectedPercentile}&thres_prob=${thresProb}`;
      if (bbox.trim()) {
        url += `&bbox=${encodeURIComponent(bbox.trim())}`;
      }
      
      const response = await fetch(url);      const data = await response.json();

      if (data.success) {
        setHazardMapData(data.data);
        setHazardData([]); // Clear probability data
        setLavaData([]); // Clear lava data
      } else {
        setError(data.error || 'Failed to load hazard map data');
      }
    } catch (err) {
      setError('Failed to load hazard map data');
      console.error('Error loading hazard map data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load lava data
  const loadLavaData = async () => {
    if (selectedPercentile === null) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/lava-exceedance?percentile=${selectedPercentile}`
      );

      const data = await response.json();

      if (data.success) {
        // Se la risposta contiene configurazione WMS, crea un oggetto fittizio con i dati WMS
        if (data.wms_config) {
          setLavaData([{
            lat: 0, // Questi valori non vengono usati per WMS
            lon: 0,
            probability: 0,
            geojson: '', // Non usato per WMS
            wms_config: data.wms_config,
            legend_config: data.legend_config,
            layer_info: data.layer_info
          }]);
        } else {
          // Fallback per dati puntuali (se ancora supportati)
          setLavaData(data.data || []);
        }
        setHazardData([]); // Clear ash probability data
        setHazardMapData([]); // Clear hazard map data
      } else {
        setError(data.error || 'Failed to load lava data');
      }
    } catch (err) {
      setError('Failed to load lava data');
      console.error('Error loading lava data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load seismic data
  const loadSeismicData = async () => {
    if (selectedPercentile === null || selectedSeismicIntensity === null) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/seismic-data?percentile=${selectedPercentile}&intensity=${selectedSeismicIntensity}`
      );

      const data = await response.json();

      if (data.success) {
        setSeismicData(data.data);
        setHazardData([]); // Clear ash probability data
        setHazardMapData([]); // Clear hazard map data
        setLavaData([]); // Clear lava data
      } else {
        setError(data.error || 'Failed to load seismic data');
      }
    } catch (err) {
      setError('Failed to load seismic data');
      console.error('Error loading seismic data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load seismic hazard map data
  const loadSeismicHazardData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let url = `${API_BASE_URL}/api/seismic-hazard-map?thres_prob=${thresProb}`;
      if (bbox.trim()) {
        url += `&bbox=${encodeURIComponent(bbox.trim())}`;
      }
      
      const response = await fetch(url);      const data = await response.json();

      if (data.success) {
        setHazardMapData(data.data); // Usa hazardMapData per le mappe di hazard sismico
        setHazardData([]); // Clear ash probability data
        setSeismicData([]); // Clear seismic probability data
        setLavaData([]); // Clear lava data
      } else {
        setError(data.error || 'Failed to load seismic hazard data');
      }
    } catch (err) {
      setError('Failed to load seismic hazard data');
      console.error('Error loading seismic hazard data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load seismic WMS data
  const loadSeismicWmsData = async () => {
    if (!selectedSeismicThreshold) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/seismic-wms?threshold=${selectedSeismicThreshold}`
      );

      const data = await response.json();

      if (data.success) {
        setSeismicWmsData(data);
        // Clear other data
        setHazardData([]);
        setHazardMapData([]);
        setLavaData([]);
        setSeismicData([]);
        console.log('Seismic WMS data loaded:', data);
      } else {
        setError(data.error || 'Failed to load seismic WMS data');
      }
    } catch (err) {
      setError('Failed to load seismic WMS data');
      console.error('Error loading seismic WMS data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Funzioni per download formati raster e immagine
  const downloadRasterLayer = (format: 'png' | 'geotiff', wmsConfig: any, timestamp: string) => {
    if (format === 'geotiff') {
      // BBOX fissa come per PNG richiesto
      const fixedBbox = '14.7967155,37.5121125,15.2444835,37.9061775';
      const geotiffUrl = `${wmsConfig.url}?` +
        `SERVICE=WMS&` +
        `VERSION=1.1.0&` +
        `REQUEST=GetMap&` +
        `LAYERS=${wmsConfig.layers}&` +
        `STYLES=&` +
        `FORMAT=image/geotiff&` +
        `BBOX=${fixedBbox}&` +
        `WIDTH=768&` +
        `HEIGHT=675&` +
        `SRS=EPSG:4326`;
      // Scarica il GeoTIFF
      const link = document.createElement('a');
      link.href = geotiffUrl;
      link.download = `lava_hazard_${selectedPercentile}_${timestamp}.tiff`;
      link.click();
      alert('GeoTIFF download started!');
    } else {
      // Per PNG, usa la funzione esistente di cattura mappa
      downloadMapAsImage(timestamp);
    }
  };

  const downloadMapAsImage = async (timestamp: string) => {
    // Carica html2canvas e JSZip se non disponibili
    const loadScript = (src: string) =>
      new Promise<void>((resolve, reject) => {
        if (document.querySelector(`script[src="${src}"]`)) return resolve();
        const script = document.createElement('script');
        script.src = src;
        script.onload = () => resolve();
        script.onerror = () => reject();
        document.body.appendChild(script);
      });

    try {
      // Carica le librerie necessarie
      await Promise.all([
        loadScript('https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js'),
        loadScript('https://cdn.jsdelivr.net/npm/jszip@3.10.1/dist/jszip.min.js')
      ]);

      const html2canvas = (window as any).html2canvas;
      const JSZip = (window as any).JSZip;

      if (!html2canvas || !JSZip) {
        alert('Required libraries not loaded. Please try again.');
        return;
      }

      // Trova il contenitore della mappa (che deve includere anche la legenda)
      const mapContainer = document.querySelector('.map-container');
      if (!mapContainer) {
        alert('Map container not found.');
        return;
      }

      // Cattura la mappa e la legenda come PNG
      const canvas = await html2canvas(mapContainer, {
        useCORS: true,
        backgroundColor: null,
        logging: false,
        scale: 2 // Migliore qualità
      });

      const dataUrl = canvas.toDataURL('image/png');
      const res = await fetch(dataUrl);
      const pngBlob = await res.blob();

      // Crea zip e aggiungi il PNG
      const zip = new JSZip();
      zip.file(`hazard_map_${timestamp}.png`, pngBlob);

      // Genera lo zip e scarica
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(zipBlob);
      link.download = `hazard_map_${timestamp}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (error) {
      alert('Failed to export map as PNG.');
      console.error(error);
    }
  };


  // Clear map layers function
  const clearMapLayers = () => {
    // Reset all data states
    setHazardData([]);
    setHazardMapData([]);
    setLavaData([]);
    setSeismicData([]);
    setSeismicWmsData(null);
    setError(null);
    
    // Show confirmation
    setTimeout(() => {
      alert('Map layers cleared successfully!');
    }, 100);
  };

  // Check if there are any active layers
  const hasActiveLayers = hazardData.length > 0 || hazardMapData.length > 0 || lavaData.length > 0 || seismicData.length > 0 || seismicWmsData !== null;

  // Download functions
  const downloadLayerData = (format: 'png' | 'geotiff' | 'geojson') => {
    let currentData: any[] = [];
    let dataType = '';
    let isWmsLayer = false;
    let wmsConfig: any = null;
    
    // Controlla se è un layer WMS seismic
    if (hazardType === 'seismic' && mapMode === 'wms' && seismicWmsData) {
      isWmsLayer = true;
      wmsConfig = seismicWmsData.wms_config;
      dataType = 'seismic_wms';
    } else if (hazardType === 'lava' && lavaData.length > 0) {
      currentData = lavaData;
      dataType = 'lava_probability';
      if ((currentData[0] as any).wms_config) {
        isWmsLayer = true;
        wmsConfig = (currentData[0] as any).wms_config;
      }
    } else if (hazardType === 'seismic' && seismicData.length > 0) {
      currentData = seismicData;
      dataType = 'seismic_probability';
    } else if (mapMode === 'probability' && hazardData.length > 0) {
      currentData = hazardData;
      dataType = 'ash_probability';
    } else if (mapMode === 'hazard' && hazardMapData.length > 0) {
      currentData = hazardMapData;
      dataType = 'hazard_map';
    }
    
    // Per layer WMS, controlla se ci sono dati o configurazione
    if (!isWmsLayer && currentData.length === 0) {
      alert('No data to download. Please load a layer first.');
      return;
    }

    const timestamp = new Date().toISOString().split('T')[0];
    
    // Gestisci download per layer WMS (apri direttamente URL PNG di GeoServer)
    if (isWmsLayer && format === 'png' && wmsConfig) {
      // BBOX fissa come da richiesta
      const fixedBbox = '14.7967155,37.5121125,15.2444835,37.9061775';
      // Costruisci URL PNG diretto di GeoServer (stesso del layer sulla mappa)
      const pngUrl = `${wmsConfig.url}?` +
        `SERVICE=WMS&` +
        `VERSION=1.1.0&` +
        `REQUEST=GetMap&` +
        `LAYERS=${wmsConfig.layers}&` +
        `STYLES=&` +
        `FORMAT=image/png&` +
        `BBOX=${fixedBbox}&` +
        `WIDTH=800&` +
        `HEIGHT=600&` +
        `SRS=EPSG:4326&` +
        `TRANSPARENT=true`;
      // Apri l'URL PNG direttamente in una nuova finestra
      window.open(pngUrl, '_blank');
      return;
    }
    
    // Gestisci formati PNG e GeoTIFF per layer raster (lava non-WMS)
    if (format === 'png' || format === 'geotiff') {
      if (hazardType === 'lava' && (currentData[0] as any).wms_config) {
        // Per layer WMS lava, scarica direttamente dal servizio
        downloadRasterLayer(format, (currentData[0] as any).wms_config, timestamp);
        return;
      } else {
        // Per layer vettoriali, genera PNG dalla mappa
        downloadMapAsImage(timestamp);
        return;
      }
    }
    
    // Per formato GeoJSON, procedi con la logica vettoriale
    // Auto-detect data format per compatibilità con codice esistente
    const dataFormat = detectDataFormat(currentData);
    
    console.log(`Using format: ${format}, detected format: ${dataFormat}`);
    
    if (format === 'geojson' || dataFormat === 'geojson') {
      // Export as GeoJSON or prepare data for Shapefile conversion
      const geoJsonFeatures = currentData.map(point => {
        const geometry = typeof point.geojson === 'string' 
          ? JSON.parse(point.geojson) 
          : point.geojson;
          
        return {
          type: "Feature",
          geometry: geometry,
          properties: {
            latitude: point.lat,
            longitude: point.lon,
            value: dataType === 'hazard_map' ? 
              (point as any).hazard : 
              (point as any).probability,
            percentile: selectedPercentile,
            ...(dataType === 'ash_probability' && { 
              ash_load_threshold: selectedAshLoad,
              probability: (point as any).probability 
            }),
            ...(dataType === 'lava_probability' && { 
              probability: (point as any).probability 
            }),
            ...(dataType === 'hazard_map' && { 
              probability_threshold: thresProb,
              hazard: (point as any).hazard
            }),
            data_type: dataType,
            hazard_type: hazardType,
            export_date: timestamp
          }
        };
      });

      const geoJson = {
        type: "FeatureCollection",
        features: geoJsonFeatures,
        metadata: {
          title: `Volcanic ${hazardType.toUpperCase()} ${dataType.includes('probability') ? 'Probability' : 'Hazard Map'} Data`,
          hazard_type: hazardType,
          data_type: dataType,
          percentile: selectedPercentile,
          ...(dataType === 'ash_probability' && { ash_load_threshold: selectedAshLoad }),
          ...(dataType === 'hazard_map' && { probability_threshold: thresProb }),
          export_date: timestamp,
          coordinate_system: "WGS84",
          data_source: "Volcanic Hazard Analysis System"
        }
      };

      // Export as GeoJSON
      const geoJsonString = JSON.stringify(geoJson, null, 2);
      const filename = `${hazardType}_${dataType}_p${selectedPercentile}_${timestamp}.geojson`;
      downloadFile(geoJsonString, filename, 'application/geo+json');
      
    } else if (dataFormat === 'wkt') {
      // Export as WKT for GIS compatibility
      const wktData = convertToWKT(currentData, dataType);
      const filename = `${mapMode}_data_p${selectedPercentile}_${timestamp}.wkt`;
      downloadFile(wktData, filename, 'text/plain');
      
    } else {
      // Export as CSV for tabular data
      const dataToExport = currentData.map(point => ({
        latitude: point.lat,
        longitude: point.lon,
        value: dataType === 'hazard_map' ? 
          (point as any).hazard : 
          (point as any).probability,
        percentile: selectedPercentile,
        ...(dataType === 'ash_probability' && { 
          ash_load_threshold: selectedAshLoad,
          probability: (point as any).probability 
        }),
        ...(dataType === 'lava_probability' && { 
          probability: (point as any).probability 
        }),
        ...(dataType === 'hazard_map' && { 
          probability_threshold: thresProb,
          hazard: (point as any).hazard
        }),
        data_type: dataType,
        hazard_type: hazardType,
        export_date: timestamp
      }));

      const csv = convertToCSV(dataToExport, dataType);
      const filename = `${hazardType}_${dataType}_p${selectedPercentile}_${timestamp}.csv`;
      downloadFile(csv, filename, 'text/csv');
    }
    
    // Show success message
    setTimeout(() => {
      const formatName = dataFormat === 'geojson' ? 'GeoJSON' : 
                        dataFormat === 'wkt' ? 'WKT' :
                        dataFormat === 'timeseries' ? 'Time Series CSV' : 'CSV';
      alert(`Data exported successfully as ${formatName} format!`);
    }, 100);
  };

  const downloadHazardCurve = (hazardCurveData: any, lat: number, lon: number, all_percentiles_curves?: any) => {
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    
    if (all_percentiles_curves && Object.keys(all_percentiles_curves).length > 0) {
      // Export con tabelle separate per percentile
      const percentiles = Object.keys(all_percentiles_curves).map(p => parseInt(p)).sort((a, b) => a - b);
      
      // Raccogli tutti i valori unici (ash_loads o intensities) (asse x)
      const allValues = new Set<number>();
      let isSeismicData = false;
      percentiles.forEach(percentile => {
        const curveData = all_percentiles_curves[percentile];
        const values = curveData.ash_loads || curveData.intensities;
        if (curveData.intensities) isSeismicData = true;
        values?.forEach((value: number) => allValues.add(value));
      });
      
      // Ordina i valori
      const sortedValues = Array.from(allValues).sort((a, b) => a - b);
      
      // Crea la struttura CSV
      const csvRows = [
        `# ${isSeismicData ? 'Seismic' : 'Volcanic'} Hazard Curves Data Export`,
        `# Export Date: ${new Date().toISOString()}`,
        `# Location: ${lat.toFixed(4)}°, ${lon.toFixed(4)}°`,
        `# Data Format: ${isSeismicData ? 'Intensity' : 'Ash Load'} (rows) vs Percentiles (columns)`,
        `# Available Percentiles: ${percentiles.join(', ')}`,
        `# Units: ${isSeismicData ? 'Macroseismic Intensity (dimensionless)' : 'Ash Load (kg/m²)'}, Probability (dimensionless)`,
        `#`,
        // Header
        [isSeismicData ? 'macroseismic_intensity' : 'ash_load_kg_m2', ...percentiles.map(p => `percentile_${p}_probability`)].join(',')
      ];
      
      // Per ogni valore, trova la probability corrispondente per ogni percentile
      sortedValues.forEach(value => {
        const row = [value.toString()];
        
        percentiles.forEach(percentile => {
          const curveData = all_percentiles_curves[percentile];
          const values = curveData.ash_loads || curveData.intensities || [];
          const probabilities = curveData.probabilities || [];
          
          // Trova l'indice corrispondente a questo valore
          const index = values.findIndex((val: number) => Math.abs(val - value) < 1e-10);
          const probability = index >= 0 ? probabilities[index] : '';
          
          row.push(probability.toString());
        });
        
        csvRows.push(row.join(','));
      });
      
      const csv = csvRows.join('\n');
      const filename = `hazard_curves_matrix_${lat.toFixed(4)}_${lon.toFixed(4)}_all_percentiles_${timestamp}.csv`;
      downloadFile(csv, filename, 'text/csv');
      
    } else {
      // Export per singolo percentile (comportamento precedente)
      // Gestisci sia ash_loads (per ash data) che intensities (per seismic data)
      const loads = hazardCurveData.ash_loads || hazardCurveData.intensities;
      const isSeismic = !!hazardCurveData.intensities;
      
      const dataToExport = loads.map((load: number, index: number) => ({
        [isSeismic ? 'macroseismic_intensity' : 'ash_load_kg_m2']: load,
        probability: hazardCurveData.probabilities[index],
        latitude: lat,
        longitude: lon,
        percentile: selectedPercentile
      }));

      const headers = Object.keys(dataToExport[0]);
      const csvRows = [
        `# ${isSeismic ? 'Seismic' : 'Volcanic'} Hazard Curve Data Export`,
        `# Export Date: ${new Date().toISOString()}`,
        `# Location: ${lat.toFixed(4)}°, ${lon.toFixed(4)}°`,
        `# Percentile: ${selectedPercentile}`,
        `#`,
        headers.join(','),
        ...dataToExport.map((row: any) => 
          headers.map(header => {
            const value = row[header];
            return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
          }).join(',')
        )
      ];
      
      const csv = csvRows.join('\n');
      const filename = `hazard_curve_${lat.toFixed(4)}_${lon.toFixed(4)}_p${selectedPercentile}_${timestamp}.csv`;
      downloadFile(csv, filename, 'text/csv');
    }
  };

  // Utility functions
  const detectDataFormat = (data: any[]) => {
    if (data.length === 0) return 'empty';
    
    const sample = data[0];
    
    // Check for GeoJSON structure
    if (sample.geojson || sample.geometry) return 'geojson';
    
    // Check for WKT geometry
    if (sample.wkt || (typeof sample.geometry === 'string' && sample.geometry.includes('POINT'))) return 'wkt';
    
    // Check for time series data
    if (sample.timestamp || sample.time || sample.date) return 'timeseries';
    
    // Default to tabular
    return 'tabular';
  };

  const convertToCSV = (data: any[], dataTypeParam: string) => {
    if (data.length === 0) return '';
    
    const headers = Object.keys(data[0]);
    const csvRows = [
      // Add CSV header with metadata
      `# Volcanic Hazard Analysis Data Export`,
      `# Export Date: ${new Date().toISOString()}`,
      `# Hazard Type: ${hazardType.toUpperCase()}`,
      `# Data Type: ${dataTypeParam}`,
      `# Percentile: ${selectedPercentile}`,
      ...(dataTypeParam === 'ash_probability' ? [`# Ash Load Threshold: ${selectedAshLoad}`] : []),
      ...(dataTypeParam === 'hazard_map' ? [`# Probability Threshold: ${thresProb}`] : []),
      `# Total Records: ${data.length}`,
      `#`,
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          // Handle special cases for CSV formatting
          if (value === null || value === undefined) return '';
          if (typeof value === 'string') return `"${value.replace(/"/g, '""')}"`;
          if (typeof value === 'number') return value.toString();
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ];
    
    return csvRows.join('\n');
  };

  const convertToWKT = (data: any[], dataTypeParam: string) => {
    // Convert to Well-Known Text format for compatibility with GIS software
    const headers = ['WKT_GEOMETRY', 'VALUE', 'PERCENTILE', 'HAZARD_TYPE', 'DATA_TYPE', ...Object.keys(data[0]).filter(k => !['lat', 'lon', 'geojson', 'geometry'].includes(k))];
    const rows = [
      headers.join('\t'),
      ...data.map(point => {
        const wktGeometry = `POINT(${point.lon} ${point.lat})`;
        const value = dataTypeParam === 'hazard_map' ? (point as any).hazard : (point as any).probability;
        const otherProps = Object.entries(point)
          .filter(([key]) => !['lat', 'lon', 'geojson', 'geometry'].includes(key))
          .map(([, val]) => val);
        return [wktGeometry, value, selectedPercentile, hazardType, dataTypeParam, ...otherProps].join('\t');
      })
    ];
    
    return rows.join('\n');
  };

  const downloadFile = (content: string, filename: string, contentType: string) => {
    const blob = new Blob([content], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="app">
      {/* Header - Copernicus Style */}
      <header className="app-header">
        <div className="header-content">
          <div className="logo-section">
            <i className="fas fa-mountain"></i>
            <h1>Volcanic Hazard Analysis</h1>
          </div>
          <div className="status-section">
            <div className="navbar-credits">
              <span>Created by </span>
              <strong>Alessio Patanè</strong>
              <span> and </span>
              <strong>Laura Sandri</strong>
              <span> with financial support of </span>
              <strong>Geo-Inquire</strong>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="dashboard-main">
        <Controls
          percentiles={percentiles}
          ashLoads={ashLoads}
          seismicIntensities={seismicIntensities}
          selectedPercentile={selectedPercentile}
          selectedAshLoad={selectedAshLoad}
          selectedSeismicIntensity={selectedSeismicIntensity}
          selectedSeismicThreshold={selectedSeismicThreshold}
          thresProb={thresProb}
          mapMode={mapMode}
          hazardType={hazardType}
          exposureTime={exposureTime}
          opacity={opacity}
          onPercentileChange={setSelectedPercentile}
          onAshLoadChange={setSelectedAshLoad}
          onSeismicIntensityChange={setSelectedSeismicIntensity}
          onThresProbChange={setThresProb}
          onMapModeChange={setMapMode}
          onHazardTypeChange={setHazardType}
          onExposureTimeChange={setExposureTime}
          onOpacityChange={setOpacity}
          downloadFormat={downloadFormat}
          hasActiveLayers={hasActiveLayers}
          bbox={bbox}
          onDownloadFormatChange={setDownloadFormat}
          onBboxChange={setBbox}
          onLoadProbabilityData={loadHazardData}
          onLoadHazardData={loadHazardMapData}
          onLoadLavaData={loadLavaData}
          onLoadSeismicData={loadSeismicData}
          onLoadSeismicHazardData={loadSeismicHazardData}
          onLoadSeismicWmsData={loadSeismicWmsData}
          onSeismicThresholdChange={setSelectedSeismicThreshold}
          onDownloadLayerData={downloadLayerData}
          onClearMapLayers={clearMapLayers}
          loading={loading}
        />
        <HazardMap 
          probabilityData={hazardData}
          hazardData={hazardMapData}
          lavaData={lavaData}
          seismicData={seismicData}
          seismicWmsData={seismicWmsData}
          mapMode={mapMode}
          hazardType={hazardType}
          opacity={opacity}
          loading={loading}
          error={error}
          onDownloadHazardCurve={downloadHazardCurve}
          onBboxChange={setBbox}
        />
      </div>


    </div>
  );
}

export default App;
